import { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../lib/auth";

type Banner = {
  id: number;
  image_path: string | null; // 서버가 null 줄 수도 있으니 방어
  memo: string | null;
  link_url: string | null;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

type FormState = {
  memo: string;
  link_url: string;
  enabled: boolean;
  sort_order: number;
  imageFile: File | null;
  imagePreview: string | null;
};

function fmtDate(s?: string | null) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return String(s);
  }
}

function isHttpUrl(v: string) {
  return /^https?:\/\//i.test(v);
}

export default function HomeBanners() {
  const [rows, setRows] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [open, setOpen] = useState<null | { mode: "create" | "edit"; row?: Banner }>(null);

  const [form, setForm] = useState<FormState>({
    memo: "",
    link_url: "",
    enabled: true,
    sort_order: 0,
    imageFile: null,
    imagePreview: null,
  });

  const isEdit = open?.mode === "edit";

  const canSubmit = useMemo(() => {
    if (!open) return false;
    if (open.mode === "create") return !!form.imageFile;
    return true;
  }, [open, form.imageFile]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await authFetch("/api/admin/banners");
      const j = await r.json();
      if (!r.ok || !j?.ok) throw new Error(j?.message || "로드 실패");
      setRows(Array.isArray(j.items) ? j.items : []);
    } catch (e: any) {
      setErr(e?.message || "로드 실패");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
    setForm({
      memo: "",
      link_url: "",
      enabled: true,
      sort_order: 0,
      imageFile: null,
      imagePreview: null,
    });
    setOpen({ mode: "create" });
  }

  function openEdit(row: Banner) {
    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
    setForm({
      memo: row.memo || "",
      link_url: row.link_url || "",
      enabled: !!row.enabled,
      sort_order: Number(row.sort_order || 0),
      imageFile: null,
      imagePreview: null,
    });
    setOpen({ mode: "edit", row });
  }

  function closeModal() {
    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
    setOpen(null);
  }

  async function submit() {
    if (!open || !canSubmit) return;

    try {
      const fd = new FormData();
      fd.set("memo", form.memo.trim());
      fd.set("link_url", form.link_url.trim());
      fd.set("enabled", String(form.enabled));
      fd.set("sort_order", String(Number.isFinite(form.sort_order) ? form.sort_order : 0));
      if (form.imageFile) fd.set("image", form.imageFile);

      const url =
        open.mode === "create"
          ? "/api/admin/banners"
          : `/api/admin/banners/${open.row!.id}`;

      const r = await authFetch(url, {
        method: open.mode === "create" ? "POST" : "PUT",
        body: fd,
      });

      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "저장 실패");

      closeModal();
      await load();
    } catch (e: any) {
      alert(e?.message || "저장 실패");
    }
  }

  async function remove(row: Banner) {
    if (!confirm(`삭제하시겠습니까?\n\n#${row.id}`)) return;
    try {
      const r = await authFetch(`/api/admin/banners/${row.id}`, { method: "DELETE" });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.message || "삭제 실패");
      await load();
    } catch (e: any) {
      alert(e?.message || "삭제 실패");
    }
  }

  return (
    <div className="jp-admin-page jp-admin-settings jp-admin-banners space-y-4">
      <div className="jp-admin-page-heading flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">배너 설정</h1>
          <p className="mt-1 text-sm text-slate-500 break-keep">
            모바일 홈 배너를 등록/수정합니다. (노출=ON + 정렬 오름차순)
          </p>
        </div>

        {/* 모바일에서 버튼이 좁아져 깨지는 것 방지 */}
        <div className="flex shrink-0 gap-2">
          <button
            onClick={load}
            className="h-10 min-w-[96px] whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            새로고침
          </button>
          <button
            onClick={openCreate}
            className="h-10 min-w-[96px] whitespace-nowrap rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            등록
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}
      {/* 모바일 카드 */}
      <div className="jp-admin-responsive-cards">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            불러오는 중...
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            데이터가 없습니다.
          </div>
        )}

        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-slate-900">#{r.id}</div>
              <div className="text-xs text-slate-500">
                {r.enabled ? "노출" : "숨김"} / 정렬 {r.sort_order}
              </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-lg border bg-slate-50">
              {r.image_path ? (
                <img
                  src={r.image_path}
                  alt={`banner-${r.id}`}
                  className="h-28 w-full object-cover"
                  onError={(e) => {
                    // 이미지가 404면 깨진 아이콘 대신 빈 박스로
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                    const parent = e.currentTarget.parentElement;
                    if (parent && !parent.querySelector("[data-empty]")) {
                      const div = document.createElement("div");
                      div.setAttribute("data-empty", "1");
                      div.className = "h-28 w-full grid place-items-center text-xs text-slate-400";
                      div.textContent = "이미지 없음";
                      parent.appendChild(div);
                    }
                  }}
                />
              ) : (
                <div className="h-28 w-full grid place-items-center text-xs text-slate-400">
                  이미지 없음
                </div>
              )}
            </div>

            <div className="mt-3 space-y-1 text-xs">
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">메모</span>
                <span className="text-right">{r.memo || "-"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">링크</span>
                <span className="text-right break-all">{r.link_url || "-"}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">업데이트</span>
                <span className="text-right">{fmtDate(r.updated_at)}</span>
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={() => openEdit(r)}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
              >
                수정
              </button>
              <button
                onClick={() => remove(r)}
                className="flex-1 rounded-lg bg-rose-600 px-3 py-2 text-sm text-white hover:bg-rose-500"
              >
                삭제
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 데스크톱 테이블 */}
      <div className="jp-admin-responsive-table rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-20 px-3 py-3 text-left">번호</th>
                <th className="w-240 px-3 py-3 text-left">배너 이미지</th>
                <th className="px-3 py-3 text-left">메모</th>
                <th className="w-260 px-3 py-3 text-left">링크</th>
                <th className="w-90 px-3 py-3 text-left">노출</th>
                <th className="w-90 px-3 py-3 text-left">정렬</th>
                <th className="w-160 px-3 py-3 text-left">업데이트</th>
                <th className="w-160 px-3 py-3 text-left">관리</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-10 text-center text-slate-500" colSpan={8}>
                    불러오는 중...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-10 text-center text-slate-500" colSpan={8}>
                    데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-3">{r.id}</td>
                    <td className="px-3 py-3">
                      <div className="h-16 w-44 overflow-hidden rounded-lg border bg-slate-50">
                        {r.image_path ? (
                          <img src={r.image_path} alt={`banner-${r.id}`} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full grid place-items-center text-xs text-slate-400">
                            이미지 없음
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">{r.memo || "-"}</td>
                    <td className="px-3 py-3 break-all text-xs">{r.link_url || "-"}</td>
                    <td className="px-3 py-3">{r.enabled ? "노출" : "숨김"}</td>
                    <td className="px-3 py-3">{r.sort_order}</td>
                    <td className="px-3 py-3 text-xs">{fmtDate(r.updated_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => remove(r)}
                          className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 등록/수정 모달 */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="border-b px-5 py-4">
              <div className="text-base font-semibold text-slate-900">
                {isEdit ? "배너 수정" : "배너 등록"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {isEdit ? "이미지는 선택하지 않으면 유지됩니다." : "등록 시 이미지는 필수입니다."}
              </div>
            </div>

            <div className="space-y-4 px-5 py-4">
              <label className="block">
                <div className="mb-1 text-xs font-medium text-slate-600">배너 이미지</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
                    setForm((p) => ({
                      ...p,
                      imageFile: f,
                      imagePreview: f ? URL.createObjectURL(f) : null,
                    }));
                  }}
                />
                <div className="mt-3 overflow-hidden rounded-xl border bg-slate-50">
                  {form.imagePreview || (isEdit && open.row?.image_path) ? (
                    <img
                      src={form.imagePreview || open.row!.image_path!}
                      alt="preview"
                      className="h-40 w-full object-cover"
                    />
                  ) : (
                    <div className="h-40 w-full grid place-items-center text-xs text-slate-400">
                      이미지 선택 필요
                    </div>
                  )}
                </div>
              </label>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <div className="mb-1 text-xs font-medium text-slate-600">노출</div>
                  <select
                    value={String(form.enabled)}
                    onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.value === "true" }))}
                    className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                  >
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </label>

                {/* ✅ 모바일에서 “정렬” 입력 편하게: +/- 버튼 + 숫자키패드 */}
                <label className="block">
                  <div className="mb-1 text-xs font-medium text-slate-600">정렬 (작을수록 먼저)</div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: (p.sort_order || 0) - 1 }))}
                      className="h-11 w-11 rounded-lg border border-slate-200 bg-white text-lg leading-none"
                      aria-label="정렬 -1"
                    >
                      −
                    </button>

                    <input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={form.sort_order}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          sort_order: Number(e.target.value) || 0,
                        }))
                      }
                      className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                    />

                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: (p.sort_order || 0) + 1 }))}
                      className="h-11 w-11 rounded-lg border border-slate-200 bg-white text-lg leading-none"
                      aria-label="정렬 +1"
                    >
                      +
                    </button>
                  </div>

                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: 0 }))}
                      className="h-10 flex-1 rounded-lg border border-slate-200 bg-white text-sm"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: 1 }))}
                      className="h-10 flex-1 rounded-lg border border-slate-200 bg-white text-sm"
                    >
                      1
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: 5 }))}
                      className="h-10 flex-1 rounded-lg border border-slate-200 bg-white text-sm"
                    >
                      5
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, sort_order: 10 }))}
                      className="h-10 flex-1 rounded-lg border border-slate-200 bg-white text-sm"
                    >
                      10
                    </button>
                  </div>
                </label>
              </div>

              <label className="block">
                <div className="mb-1 text-xs font-medium text-slate-600">메모</div>
                <input
                  value={form.memo}
                  onChange={(e) => setForm((p) => ({ ...p, memo: e.target.value }))}
                  className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                  placeholder="관리용 메모"
                />
              </label>

              <label className="block">
                <div className="mb-1 text-xs font-medium text-slate-600">링크 URL (선택)</div>
                <input
                  value={form.link_url}
                  onChange={(e) => setForm((p) => ({ ...p, link_url: e.target.value }))}
                  className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                  placeholder="https://..."
                />
                {!!form.link_url && !isHttpUrl(form.link_url) && (
                  <div className="mt-1 text-xs text-amber-600">
                    http:// 또는 https:// 로 시작하는 URL을 권장합니다.
                  </div>
                )}
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                onClick={closeModal}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className={[
                  "rounded-lg px-4 py-2 text-sm font-medium text-white",
                  canSubmit ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-300 cursor-not-allowed",
                ].join(" ")}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
